import {MaterialType} from '@domain/materials'
import {
  PageDailyProductionPlanning,
  PageMonthlyProductionPlanning,
  ProductionPlanningHostProvider
} from '@features/production-planning'
import {MaterialOrderPermissionsProvider} from '@features/storage-levels'
import {TourBanner} from '@hconnect/uikit/src/lib2'
import {useUpmPlantContext} from '@hconnect/upm'
import {FeatureFlagConfig, FeatureFlagKey} from '@hproduce/feature-flags'
import React, {Suspense} from 'react'
import {Navigate, Routes, Route} from 'react-router'

import {subPageDynamicImportMap} from '../dynamicImports'
import {FeatureGuardedRoute} from '../routing/FeatureGuardedRoute'
import {getRelPath, getUrl} from '../routing/helpers'
import {PermissionGuardedRoute} from '../routing/PermissionGuardedRoute'
import {PLANT_ROUTES, RouteName, ROUTES} from '../routing/router'
import {OptimizerNotification} from '../shared/components/OptimizerNotification'
import {PlannerPageSkeleton} from '../shared/components/skeletons/PlannerPageSkeleton'
import {mainPagesToPermissionsMap} from '../shared/permissions'
import {useStockMaterialOrderPermissions} from '../shared/permissions/useStockMaterialOrderPermissions'

import {PageNoOptimizedPlans} from './page-optimizer/noOptimizedPlans/PageNoOptimizedPlans'
import {PageOptimizerSummary} from './page-optimizer/PageOptimizerSummary'
import {MaterialIdGuard} from './page-stock/materialOrders/MaterialIdGuard'
import {PageMaterialOrders, PageMaterials, PageSilos, PageStock} from './page-stock/PageStock'

const PageElectricity = React.lazy(subPageDynamicImportMap[RouteName.ELECTRICITY_FORECAST])
const PageSalesForecast = React.lazy(subPageDynamicImportMap[RouteName.SALES_FORECAST])
const PageControlOperation = React.lazy(subPageDynamicImportMap[RouteName.OPERATOR_VIEW])
const PagePlanning = React.lazy(subPageDynamicImportMap[RouteName.PRODUCTION_PLANNING])
const PageOptimizer = React.lazy(subPageDynamicImportMap[RouteName.OPTIMIZER])

const StockIndexRedirect: React.FC = () => {
  const {plantId: plantCode} = useUpmPlantContext()
  return (
    <Navigate
      to={`${getUrl(PLANT_ROUTES.STOCK.SILOS.path, {plantCode})}?materialType=${MaterialType.Cement}`}
      replace
    />
  )
}

/**
 * Enforces HCEM plant permissions for the material orders shown on the daily planning
 * stock development chart. Suite intentionally has no provider here (lean roles = all allowed).
 */
const DailyProductionPlanningWithPermissions: React.FC = () => {
  const permissions = useStockMaterialOrderPermissions()
  return (
    <MaterialOrderPermissionsProvider permissions={permissions}>
      <PageDailyProductionPlanning />
    </MaterialOrderPermissionsProvider>
  )
}

/**
 * Renders the production-planning tour banner + optimizer notification for HCEM.
 * HCEM is the only host that mounts a `GuidedToursProvider` (via `PlannerToursProvider`
 * in `PagePlant`), so tour UI is added here rather than inside the feature. Suite
 * renders the feature without this wrapper and therefore without tour UI.
 */
const PagePlanningWithGuidedTours: React.FC = () => (
  <ProductionPlanningHostProvider HeaderNotification={OptimizerNotification} inverted>
    <TourBanner sx={{mt: 3, mx: {xs: 2, lg: 6}}} />
    <PagePlanning />
  </ProductionPlanningHostProvider>
)

export const GuardedRoutes: React.FC = () => {
  return (
    <Suspense fallback={<PlannerPageSkeleton />}>
      <Routes>
        <Route
          path={getRelPath(PLANT_ROUTES.STOCK.path)}
          element={
            <FeatureGuardedRoute
              configurationName={FeatureFlagConfig.Suite}
              featureKey={FeatureFlagKey.StorageLevels}
            />
          }
        >
          <Route
            path=""
            element={
              <PermissionGuardedRoute
                requiredPermissions={mainPagesToPermissionsMap.stock.permissionTypes}
                operator={mainPagesToPermissionsMap.stock.operator}
              />
            }
          >
            <Route path="" element={<PageStock />}>
              <Route index element={<StockIndexRedirect />} />
              <Route path={getRelPath(PLANT_ROUTES.STOCK.SILOS.path)} element={<PageSilos />} />
              <Route
                path={getRelPath(PLANT_ROUTES.STOCK.MATERIALS.path)}
                element={<PageMaterials />}
              />
            </Route>
            <Route path={getRelPath(PLANT_ROUTES.STOCK.MATERIALS.path)}>
              <Route
                path={getRelPath(PLANT_ROUTES.STOCK.MATERIALS.ORDERS.path)}
                element={
                  <FeatureGuardedRoute
                    configurationName={FeatureFlagConfig.Common}
                    featureKey={FeatureFlagKey.RawMaterialsPlanning}
                  />
                }
              >
                <Route
                  path=""
                  element={
                    <PermissionGuardedRoute requiredPermissions={['VIEW_MATERIAL_ORDERS']} />
                  }
                >
                  <Route path="" element={<MaterialIdGuard />}>
                    <Route path="" element={<PageMaterialOrders />} />
                  </Route>
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>
        <Route
          path={getRelPath(PLANT_ROUTES.ELECTRICITY_FORECAST.path)}
          element={
            <FeatureGuardedRoute
              configurationName={FeatureFlagConfig.Suite}
              featureKey={FeatureFlagKey.ElectricityPriceForecast}
            />
          }
        >
          <Route
            path=""
            element={
              <PermissionGuardedRoute
                requiredPermissions={mainPagesToPermissionsMap.electricityForecast.permissionTypes}
                operator={mainPagesToPermissionsMap.electricityForecast.operator}
              />
            }
          >
            <Route path="" element={<PageElectricity />} />
          </Route>
        </Route>
        <Route
          path={getRelPath(PLANT_ROUTES.SALES_FORECAST.path)}
          element={
            <FeatureGuardedRoute
              configurationName={FeatureFlagConfig.Suite}
              featureKey={FeatureFlagKey.SalesForecast}
            />
          }
        >
          <Route
            path=""
            element={
              <PermissionGuardedRoute
                requiredPermissions={mainPagesToPermissionsMap.salesForecast.permissionTypes}
                operator={mainPagesToPermissionsMap.salesForecast.operator}
              />
            }
          >
            <Route path="" element={<PageSalesForecast />} />
          </Route>
        </Route>
        <Route
          path={getRelPath(PLANT_ROUTES.PRODUCTION_PLANNING.path)}
          element={
            <FeatureGuardedRoute
              configurationName={FeatureFlagConfig.Suite}
              featureKey={FeatureFlagKey.ProductionPlanning}
            />
          }
        >
          <Route
            path=""
            element={
              <PermissionGuardedRoute
                requiredPermissions={mainPagesToPermissionsMap.productionPlanning.permissionTypes}
                operator={mainPagesToPermissionsMap.productionPlanning.operator}
              />
            }
          >
            <Route path="" element={<PagePlanningWithGuidedTours />}>
              <Route
                index
                element={
                  <Navigate to={getRelPath(PLANT_ROUTES.PRODUCTION_PLANNING.DAILY.path)} replace />
                }
              />
              <Route
                path={getRelPath(PLANT_ROUTES.PRODUCTION_PLANNING.DAILY.path)}
                element={<DailyProductionPlanningWithPermissions />}
              />
              <Route
                path={getRelPath(PLANT_ROUTES.PRODUCTION_PLANNING.MONTHLY.path)}
                element={<PageMonthlyProductionPlanning />}
              />
            </Route>
          </Route>
        </Route>
        <Route
          path={getRelPath(PLANT_ROUTES.OPTIMIZER.path)}
          element={
            <FeatureGuardedRoute
              configurationName={FeatureFlagConfig.Suite}
              featureKey={FeatureFlagKey.Optimizer}
            />
          }
        >
          <Route
            path=""
            element={
              <PermissionGuardedRoute
                requiredPermissions={mainPagesToPermissionsMap.optimizer.permissionTypes}
              />
            }
          >
            <Route path="" element={<PageOptimizer />}>
              <Route
                index
                element={<Navigate to={getRelPath(PLANT_ROUTES.OPTIMIZER.SUMMARY.path)} replace />}
              />
              <Route
                path={getRelPath(PLANT_ROUTES.OPTIMIZER.SUMMARY.path)}
                element={<PageOptimizerSummary />}
              />
              <Route
                path={getRelPath(PLANT_ROUTES.OPTIMIZER.NO_PLANS.path)}
                element={<PageNoOptimizedPlans />}
              />
            </Route>
          </Route>
        </Route>
        <Route
          path={getRelPath(PLANT_ROUTES.OPERATOR_VIEW.path)}
          element={
            <FeatureGuardedRoute
              configurationName={FeatureFlagConfig.Suite}
              featureKey={FeatureFlagKey.OperatorView}
            />
          }
        >
          <Route
            path=""
            element={
              <PermissionGuardedRoute
                requiredPermissions={mainPagesToPermissionsMap.operatorView.permissionTypes}
                operator={mainPagesToPermissionsMap.operatorView.operator}
              />
            }
          >
            <Route path="" element={<PageControlOperation />} />
          </Route>
        </Route>
        <Route
          path="*"
          element={<Navigate to={getUrl(ROUTES.BASE.NOT_FOUND.path, {})} replace />}
        />
      </Routes>
    </Suspense>
  )
}
